import axios, { AxiosResponse } from 'axios';

import { Dependencies } from '@dispatch';
import { LocalPaymentMethods, Locale, ShopBundle } from '@types-internal';
import { BrowserInfo } from '@types-internal/AdyenTypes';
import {
  InitiatePaymentProcessRequestData,
  InitiateRetryPaymentCreditCardRequestData,
  InitiateRetryPaymentLocalRequestData,
  RetryPaymentData,
} from '@types-internal/RetryTypes';
import { getDefaultConfig } from '@utils/configUtils';
import { convertMajorUnitsToMinorUnits } from '@utils/utils';

import { captureException } from './sentryService';

export const getRetryPaymentData = async ({
  domain,
  shopBundle,
  locale,
  orderId,
  customerId,
  originalUrl,
}: {
  domain: string;
  shopBundle: ShopBundle;
  locale: Locale;
  orderId: string;
  customerId: string;
  originalUrl: string;
}) => {
  try {
    const { retryPaymentBaseUrl } = getDefaultConfig();
    const result: AxiosResponse<RetryPaymentData> = await axios({
      method: 'get',
      url: `${retryPaymentBaseUrl}/${customerId}/retry-payment-data/${orderId}/${shopBundle}`,
      headers: {
        'Content-type': 'application/json',
        'X-Original-Host': domain,
        'Accept-Language': locale,
      },
      params: { originalURL: originalUrl },
    });
    if (!result) {
      throw new Error('Internal error while setting favorite card');
    }
    return result;
  } catch (error: any) {
    throw captureException({ error });
  }
};

export type RetryPaymentIdealData = {
  transactionId?: string;
  transactionUrl?: string;
};

export const getRetryPaymentIdealData = async ({
  orderId,
  customerId,
  domain,
  isMobileApp,
  locale,
}: {
  orderId: string;
  customerId: string;
  domain: string;
  isMobileApp: boolean;
  locale: Locale;
}) => {
  try {
    const { retryPaymentBaseUrl } = getDefaultConfig();
    const result: AxiosResponse<RetryPaymentIdealData> = await axios({
      method: 'GET',
      url: `${retryPaymentBaseUrl}/${customerId}/retry-ideal/${orderId}`,
      withCredentials: true,
      headers: {
        'Content-type': 'application/json',
        'X-Original-Host': domain,
        'Accept-Language': locale,
      },
      params: { isMobileApp: isMobileApp },
    });
    if (!result) {
      throw new Error('Internal error while fetching iDEAL retry data');
    }
    return result;
  } catch (error: any) {
    throw captureException({ error });
  }
};

export const getRetryData =
  ({
    orderId,
    customerId,
    shopBundle,
    pspResponseUrl,
    isAuthorizationResult,
  }: {
    orderId: string;
    customerId: string;
    shopBundle: ShopBundle;
    pspResponseUrl: string;
    isAuthorizationResult: boolean;
  }) =>
  async ({ retryApi, retrySuccessWithData, processPaymentAuthorisationSuccess }: Dependencies) => {
    try {
      const [retryPaymentData, processAuthorizationData] = await Promise.all([
        retryApi.getRetryPaymentData(customerId, orderId, shopBundle),
        isAuthorizationResult
          ? retryApi.processPaymentAuthorization({ customerId, pspResponseUrl })
          : Promise.resolve(undefined),
      ]);

      retrySuccessWithData?.({
        ...retryPaymentData,
        idealAvailable: retryPaymentData.retryPaymentMethod === 'IDEAL',
      });

      if (processAuthorizationData) {
        processPaymentAuthorisationSuccess(processAuthorizationData);
      }
    } catch (error: any) {
      //handle only presentational error ,
    }
  };

export const initiatePayment =
  (payload: InitiateRetryPaymentLocalRequestData | InitiateRetryPaymentCreditCardRequestData) =>
  async ({
    retryApi,
    initiateRetryPaymentFailure,
    initiateRetryPaymentStart,
    initiateRetryPaymentSuccess,
  }: Dependencies) => {
    try {
      initiateRetryPaymentStart();
      const responseData = await retryApi.initiatePayment(payload);
      initiateRetryPaymentSuccess(
        responseData.action,
        String(responseData.paymentId),
        responseData.retryPaymentStatus,
        responseData.authorizationId,
      );
    } catch (error: any) {
      initiateRetryPaymentFailure();
    }
  };

export const processPaymentAuthorisation =
  (payload: InitiatePaymentProcessRequestData) =>
  async ({
    retryApi,
    processPaymentAuthorisationFailure,
    processPaymentAuthorisationStart,
    processPaymentAuthorisationSuccess,
  }: Dependencies) => {
    try {
      processPaymentAuthorisationStart();
      const responseData = await retryApi.processPaymentAuthorization(payload);
      processPaymentAuthorisationSuccess(responseData);
    } catch (error: any) {
      processPaymentAuthorisationFailure();
    }
  };

export const getLocalPaymentMethods = async (
  siteId: number,
  customerId: string,
  amount: number,
): Promise<LocalPaymentMethods> => {
  const { retryPaymentBaseUrl } = getDefaultConfig();

  const minorUnitsAmount = convertMajorUnitsToMinorUnits(amount);
  const result: AxiosResponse<LocalPaymentMethods> = await axios({
    method: 'GET',
    url: `${retryPaymentBaseUrl}/${customerId}/retry-payment-local-methods/${siteId}/${minorUnitsAmount}`,
    headers: {
      'Content-type': 'application/json',
    },
  });

  if (!result) {
    throw new Error('Error while getting LocalPaymentMethods');
  }

  return result.data;
};

export const collectBrowserInfo = (): BrowserInfo => {
  const d = new Date();
  const browserTimeZoneOffset = d.getTimezoneOffset() ? d.getTimezoneOffset() : 0;

  return {
    browserScreenWidth: window.screen.width,
    browserScreenHeight: window.screen.height,
    browserColorDepth: window.screen.colorDepth,
    browserUserAgent: window.navigator.userAgent || 'None',
    browserTimeZoneOffset,
    browserLanguage: window.navigator.language,
    browserJavaEnabled: window.navigator.javaEnabled(),
  };
};
